import React from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import dayjs from 'dayjs'
import CurrencyFormat from 'react-currency-format'
import CampaignStatusTag from './CampaignStatusTag'
import CampaignRowActions from './CampaignRowActions'

const COLUMN_WIDTHS = {
  type: 'w-[120px]',
  period: 'w-[150px]',
  budget: 'w-[120px]',
  participants: 'w-[140px]',
  performance: 'w-[150px]',
  status: 'w-[120px]'
}

function Checkbox({ checked, onChange }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 rounded border border-border-lines accent-primary"
    />
  )
}

function PeriodCell({ start, end }) {
  if (!start || !end) return <span className="text-sm text-secondary">--</span>

  const startLabel = dayjs(start).format('MMM D')
  const endLabel = dayjs(end).format('MMM D YYYY')
  const daysLeft = dayjs(end).diff(dayjs(), 'day')

  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-sm text-dark-blue">{startLabel} - {endLabel}</p>
      <p className="text-xs text-secondary">{daysLeft > 0 ? `${daysLeft} days left` : 'Ended'}</p>
    </div>
  )
}

function BudgetCell({ budget, spent }) {
  if (spent == null) {
    return (
      <CurrencyFormat
        value={budget ?? 0}
        displayType="text"
        thousandSeparator
        prefix="NGN "
        renderText={(value) => <p className="text-sm text-dark-blue whitespace-nowrap">{value}</p>}
      />
    )
  }

  const percent = budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0

  return (
    <div className="flex flex-col gap-1 w-full">
      <CurrencyFormat
        value={budget ?? 0}
        displayType="text"
        thousandSeparator
        prefix="NGN "
        renderText={(value) => <p className="text-sm text-dark-blue whitespace-nowrap">{value}</p>}
      />
      <p className="text-xs text-secondary">{percent}% used</p>
      <div className="h-1 w-full rounded-full bg-background overflow-hidden">
        <div className="h-full bg-success rounded-full" style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}

export default withTranslation()(function CampaignsTable({
  t, campaigns = [], isLoading, selected, onToggleSelect, onViewDetails, onPreview, onTogglePause, onDelete, canPause, canDelete
}) {
  const columns = t('promo.listing.columns', { returnObjects: true })

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[1000px]">
        <div className="flex items-center bg-background border-b border-border-lines">
          <div className="w-10 shrink-0" />
          <div className="flex-1 min-w-[220px] px-2 py-2.5 text-sm font-semibold text-dark-blue">{columns.campaign}</div>
          <div className={`${COLUMN_WIDTHS.type} shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue`}>{columns.type}</div>
          <div className={`${COLUMN_WIDTHS.period} shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue`}>{columns.period}</div>
          <div className={`${COLUMN_WIDTHS.budget} shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue`}>{columns.budget}</div>
          <div className={`${COLUMN_WIDTHS.participants} shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue`}>{columns.participants}</div>
          <div className={`${COLUMN_WIDTHS.performance} shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue`}>{columns.performance}</div>
          <div className={`${COLUMN_WIDTHS.status} shrink-0 px-2 py-2.5 text-sm font-semibold text-dark-blue`}>{columns.status}</div>
          <div className="w-12 shrink-0" />
        </div>

        {
          !isLoading && campaigns.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <div className="w-20 h-20 rounded-full bg-icon-bg flex items-center justify-center">
                <UIcons.UilMegaphone size={36} className="text-primary" />
              </div>
              <p className="text-base font-semibold text-dark-blue">{t('promo.listing.empty_title')}</p>
              <p className="text-sm text-secondary max-w-sm">{t('promo.listing.empty_hint')}</p>
            </div>
          )
        }

        {
          campaigns.map((campaign) => {
            const isPaused = (campaign.status ?? '').toLowerCase() === 'paused'
            const participants = campaign.participants_count ?? campaign.views_count ?? 0
            const performance = campaign.performance_multiplier
              ? (String(campaign.performance_multiplier).includes('%') ? campaign.performance_multiplier : `${campaign.performance_multiplier}x`)
              : '--'

            return (
              <div key={campaign.id} className="flex items-center border-b border-border-lines h-24">
                <div className="w-10 shrink-0 flex items-center justify-center">
                  <Checkbox checked={!!selected[campaign.id]} onChange={() => onToggleSelect(campaign.id)} />
                </div>

                <div className="flex-1 min-w-[220px] flex items-center gap-3 px-2">
                  <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center shrink-0 overflow-hidden">
                    {
                      campaign.thumbnail_stream
                        ? <img src={campaign.thumbnail_stream} alt="" className="w-full h-full object-cover" />
                        : <UIcons.UilMegaphone size={20} className="text-white" />
                    }
                  </div>
                  <p className="text-sm text-dark-blue truncate capitalize">{campaign.title}</p>
                </div>

                <div className={`${COLUMN_WIDTHS.type} shrink-0 px-2`}>
                  <p className="text-sm text-dark-blue capitalize truncate">{campaign.type ?? '--'}</p>
                </div>

                <div className={`${COLUMN_WIDTHS.period} shrink-0 px-2`}>
                  <PeriodCell start={campaign.start} end={campaign.end} />
                </div>

                <div className={`${COLUMN_WIDTHS.budget} shrink-0 px-2`}>
                  <BudgetCell budget={campaign.budget} spent={campaign.amount_spent} />
                </div>

                <div className={`${COLUMN_WIDTHS.participants} shrink-0 px-2`}>
                  <CurrencyFormat
                    value={participants}
                    displayType="text"
                    thousandSeparator
                    renderText={(value) => <p className="text-sm text-dark-blue">{value}</p>}
                  />
                </div>

                <div className={`${COLUMN_WIDTHS.performance} shrink-0 px-2`}>
                  <p className="text-sm text-dark-blue">{performance}</p>
                </div>

                <div className={`${COLUMN_WIDTHS.status} shrink-0 px-2`}>
                  <CampaignStatusTag status={campaign.status} />
                </div>

                <div className="w-12 shrink-0 flex items-center justify-center">
                  <CampaignRowActions
                    campaign={campaign}
                    isPaused={isPaused}
                    canPause={canPause}
                    canDelete={canDelete}
                    onViewDetails={onViewDetails}
                    onPreview={onPreview}
                    onTogglePause={onTogglePause}
                    onDelete={onDelete}
                  />
                </div>
              </div>
            )
          })
        }
      </div>
    </div>
  )
})
